import { BadRequestException, Injectable } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { ReadinessFacts, ReadinessReport, buildReadiness } from '@cart/shared-utils';
import { BrandingService } from '../branding/branding.service';

/**
 * Whether a store is set up enough to sell, and how well set up it is.
 *
 * One query pass, one report, used by the admin banner, the checklist page and
 * the gate on creating a product — so all three agree. A gate that computed
 * readiness differently from the banner would block a merchant the banner had
 * just told them was ready, which is worse than having no gate at all.
 */
@Injectable()
export class ReadinessService {
  constructor(
    private readonly prisma: PrismaService,
    private readonly branding: BrandingService
  ) {}

  async report(storeId: string): Promise<ReadinessReport> {
    return buildReadiness(await this.facts(storeId));
  }

  /**
   * Refuses when a store cannot sell yet.
   *
   * Enforced here rather than only in the UI, deliberately. A checklist the
   * browser honours is a suggestion — the request that matters is the one that
   * skips the screen.
   */
  async assertCanSell(storeId: string): Promise<void> {
    const report = await this.report(storeId);
    if (report.canSell) return;

    const missing = report.missing
      .filter(i => i.group === 'required')
      .map(i => i.label)
      .join(', ');

    throw new BadRequestException(
      `This store is not ready to sell yet. Still needed: ${missing}.`
    );
  }

  private async facts(storeId: string): Promise<ReadinessFacts> {
    const store = await this.prisma.store.findUnique({
      where: { id: storeId },
      select: {
        city: true, state: true, gstin: true,
        themeId: true, storefrontTemplateId: true, organizationId: true,
      },
    });

    const organizationId = store?.organizationId ?? '';

    const [
      categoryCount, taxProfileCount, discountCount, shippingChannels,
      storageCount, emailSettings, branding, seo,
    ] = await Promise.all([
      this.prisma.category.count({ where: { storeId } }),
      this.prisma.taxSlabProfile.count({ where: { storeId } }),
      this.prisma.discountCode.count({ where: { storeId } }),
      this.prisma.shippingChannel.findMany({
        where: { storeId, isActive: true },
        select: { updatedBy: true },
      }),
      this.prisma.merchantStorageConfig.count({ where: { organizationId } }).catch(() => 0),
      // EmailSettings is keyed by store, not organisation.
      this.prisma.emailSettings.findFirst({ where: { storeId } }).catch(() => null),
      this.prisma.storeBranding.findUnique({
        where: { storeId },
        select: { logoAssetId: true, faqs: true, openingHours: true },
      }),
      this.branding.completeness(storeId).catch(() => null),
    ]);

    const faqs = branding?.faqs;
    const hours = branding?.openingHours;

    return {
      hasCity: !!store?.city?.trim(),
      hasState: !!store?.state?.trim(),
      hasGstin: !!store?.gstin?.trim(),
      categoryCount,
      taxProfileCount,
      hasStorage: storageCount > 0,
      hasShippingChannel: shippingChannels.length > 0,
      // Seeded rows are stamped 'system'; a merchant who has never opened the
      // shipping screen should be told a default is in force rather than seeing
      // a tick they did not earn.
      shippingSeeded:
        shippingChannels.length > 0 &&
        shippingChannels.every((channel: { updatedBy: string }) => channel.updatedBy === 'system'),

      hasLogo: !!branding?.logoAssetId,
      seoPercent: seo?.percent ?? 0,
      hasThemeChosen: !!store?.themeId,
      hasTemplateChosen: !!store?.storefrontTemplateId,
      faqCount: Array.isArray(faqs) ? faqs.length : 0,
      discountCount,
      hasEmailSettings: !!emailSettings,
      hasOpeningHours: Array.isArray(hours) && hours.length > 0,
    };
  }
}
